import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, X, Sparkles, Loader2, Users, FileSpreadsheet } from "lucide-react";
import { Link } from "wouter";
import { useState, useEffect, useRef } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useCustomerEvidenceAccess } from "@/hooks/use-customer-evidence-access";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AI_PROGRESS_MESSAGES = [
  "Analyzing persona description…",
  "Identifying core beliefs and worldview…",
  "Mapping emotional drivers and pain points…",
  "Building trust triggers and objections…",
  "Crafting language patterns and voice…",
  "Determining purchase decision drivers…",
  "Profiling awareness stage and funnel fit…",
  "Generating persuasion hooks…",
  "Assembling the full persona profile…",
];

const AWARENESS_STAGES = [
  { value: "unaware", label: "Unaware" },
  { value: "problem_aware", label: "Problem Aware" },
  { value: "solution_aware", label: "Solution Aware" },
  { value: "product_aware", label: "Product Aware" },
  { value: "most_aware", label: "Most Aware" },
];

function ChipEditor({ items, onChange, placeholder }: { items: string[]; onChange: (items: string[]) => void; placeholder?: string }) {
  const [input, setInput] = useState("");
  const addItem = () => {
    const val = input.trim();
    if (val && !items.includes(val)) {
      onChange([...items, val]);
      setInput("");
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <Badge key={i} variant="secondary" className="text-xs rounded-full px-2.5 py-1 gap-1">
            {item}
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="ml-0.5 hover:text-destructive" data-testid={`button-remove-chip-${i}`}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          placeholder={placeholder || "Type and press Enter"}
          className="text-sm"
          data-testid="input-chip"
        />
        <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="button-add-chip">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function PersonaCreatePage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const currentBrandRef = useRef(brandId);
  const brandVersionRef = useRef(0);
  if (currentBrandRef.current !== brandId) {
    currentBrandRef.current = brandId;
    brandVersionRef.current += 1;
  }
  const previousBrandRef = useRef(brandId);
  const evidenceRequestInFlightRef = useRef(false);
  const confirmationBrandRef = useRef<string | undefined>(undefined);
  const confirmationBrandVersionRef = useRef(brandVersionRef.current);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const evidenceAccess = useCustomerEvidenceAccess(brandId);
  const evidenceEnabled = evidenceAccess.enabled;
  const canGenerateEvidence = evidenceAccess.canGenerate;

  const [aiDescription, setAiDescription] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [evidencePersonaCount, setEvidencePersonaCount] = useState(10);
  const [aiProgressIndex, setAiProgressIndex] = useState(0);
  const [mode, setMode] = useState<"choose" | "ai" | "manual">("choose");

  // Brands with zero personas get a third choose-screen option that ideates
  // the 10 best starter personas from the brand DNA + products in one run.
  const { data: existingPersonas } = useQuery<{ id: string }[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const showStarterOption = Array.isArray(existingPersonas) && existingPersonas.length === 0;

  const evidenceMutation = useMutation({
    mutationFn: async (submittingBrandId: string) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${submittingBrandId}/generate-starter-personas`,
        { source: "customer_evidence", count: evidencePersonaCount },
      );
      return res.json();
    },
  });

  useEffect(() => {
    if (previousBrandRef.current !== brandId) {
      previousBrandRef.current = brandId;
      confirmationBrandRef.current = undefined;
      setEvidenceDialogOpen(false);
    }
  }, [brandId]);

  const handleEvidenceCardClick = () => {
    if (!brandId || !evidenceEnabled || !canGenerateEvidence || evidenceRequestInFlightRef.current) return;
    confirmationBrandRef.current = brandId;
    confirmationBrandVersionRef.current = brandVersionRef.current;
    setEvidenceDialogOpen(true);
  };

  const handleEvidenceConfirm = async () => {
    if (evidenceRequestInFlightRef.current) return;
    const submittingBrandId = confirmationBrandRef.current;
    const submittingBrandVersion = confirmationBrandVersionRef.current;
    if (
      !submittingBrandId
      || !evidenceEnabled
      || !canGenerateEvidence
      || submittingBrandId !== currentBrandRef.current
      || submittingBrandVersion !== brandVersionRef.current
    ) {
      setEvidenceDialogOpen(false);
      confirmationBrandRef.current = undefined;
      return;
    }

    evidenceRequestInFlightRef.current = true;
    setEvidenceDialogOpen(false);
    try {
      const result = await evidenceMutation.mutateAsync(submittingBrandId);
      if (
        !mountedRef.current
        || currentBrandRef.current !== submittingBrandId
        || brandVersionRef.current !== submittingBrandVersion
      ) return;

      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${submittingBrandId}/personas`],
      });
      const duplicate = result?.duplicate === true
        || result?.alreadyRunning === true
        || result?.status === "already_running";
      toast({
        title: duplicate ? "Generation already running" : `Generating ${evidencePersonaCount} ${evidencePersonaCount === 1 ? "persona" : "personas"} from customer evidence`,
        description: duplicate
          ? "A customer evidence persona batch is already running for this brand."
          : "This adds new personas without replacing current ones. Generation runs in the background.",
      });
      navigate(`/brands/${submittingBrandId}/personas`);
    } catch (error) {
      if (
        !mountedRef.current
        || currentBrandRef.current !== submittingBrandId
        || brandVersionRef.current !== submittingBrandVersion
      ) return;
      toast({
        title: "Generation failed",
        description: error instanceof Error ? error.message : "Could not start persona generation.",
        variant: "destructive",
      });
    } finally {
      evidenceRequestInFlightRef.current = false;
    }
  };

  const starterMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/generate-starter-personas`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
      toast({
        title: "Generating 10 starter personas",
        description: "They'll fill in on the personas list as they complete. You can navigate away safely.",
      });
      navigate(`/brands/${brandId}/personas`);
    },
    onError: (e: Error) => toast({ title: "Generation failed", description: e.message, variant: "destructive" }),
  });

  useEffect(() => {
    if (!aiGenerating) {
      setAiProgressIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setAiProgressIndex((prev) =>
        prev < AI_PROGRESS_MESSAGES.length - 1 ? prev + 1 : prev,
      );
    }, 12000);
    return () => clearInterval(interval);
  }, [aiGenerating]);

  const [form, setForm] = useState({
    name: "",
    status: "draft",
    awarenessStage: "problem_aware",
    summary: "",
    worldview: "",
    primaryPain: "",
    desiredOutcome: "",
    deepFear: "",
    coreBeliefsJson: [] as string[],
    objectionsJson: [] as string[],
    trustTriggersJson: [] as string[],
    purchaseDriversJson: [] as string[],
    emotionalHooksJson: [] as string[],
    languagePatternsJson: [] as string[],
    customerVoiceQuotesJson: [] as string[],
    secondaryPainsJson: [] as string[],
    exclusionsJson: [] as string[],
    notes: "",
  });

  const handleAiGenerate = async () => {
    if (!aiDescription.trim()) {
      toast({ title: "Please describe the persona first", variant: "destructive" });
      return;
    }
    setAiGenerating(true);
    try {
      const res = await apiRequest("POST", `/api/ai/generate-persona`, { description: aiDescription, brandId });
      const data = await res.json();

      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });

      toast({
        title: "Persona is generating",
        description: "You'll be notified when it's ready. You can navigate away safely.",
      });

      navigate(`/brands/${brandId}/personas`);
    } catch (e: any) {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
      setAiGenerating(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const slug = data.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const res = await apiRequest("POST", `/api/brands/${brandId}/personas`, { ...data, slug });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
      toast({ title: "Persona created" });
      navigate(`/brands/${brandId}/personas/${data.id}`);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const setField = (name: string, value: any) => setForm(f => ({ ...f, [name]: value }));

  if (mode === "choose") {
    return (
      <div className="p-8 max-w-3xl mx-auto space-y-8">
        <div className="flex items-center gap-3 fade-in-up">
          <Link href={`/brands/${brandId}/personas`}>
            <Button variant="ghost" size="icon" data-testid="button-back-personas"><ArrowLeft className="h-4 w-4" /></Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Create Persona</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Build a persuasion profile for campaign planning</p>
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 ${showStarterOption || evidenceEnabled ? "lg:grid-cols-3" : ""} gap-4 fade-in-up-delay-1`}>
          {showStarterOption && !evidenceEnabled && (
            <Card
              className={`border-primary/30 bg-primary/[0.02] hover:border-primary/50 transition-colors ${starterMutation.isPending ? "opacity-70 pointer-events-none" : "cursor-pointer"}`}
              onClick={() => { if (!starterMutation.isPending) starterMutation.mutate(); }}
              data-testid="card-starter-personas"
            >
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  {starterMutation.isPending ? (
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  ) : (
                    <Users className="h-5 w-5 text-primary" />
                  )}
                </div>
                <h3 className="font-semibold">Generate 10 Starter Personas</h3>
                <p className="text-sm text-muted-foreground">Ideate the 10 best customer personas from your brand DNA and products, all created in the background</p>
              </CardContent>
            </Card>
          )}
          {evidenceEnabled && (
            <Card
              className="border-primary/30 bg-primary/[0.02] transition-colors hover:border-primary/50"
              data-testid="card-evidence-personas"
            >
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  {evidenceMutation.isPending ? (
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-5 w-5 text-primary" />
                  )}
                </div>
                <h3 className="font-semibold">Generate from customer evidence</h3>
                <p className="text-sm text-muted-foreground">
                  {canGenerateEvidence
                    ? "Generate 1–10 personas from active customer evidence. Existing personas stay as they are."
                    : "Import and activate customer evidence in Knowledge Base before generating personas."}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-1"
                  onClick={handleEvidenceCardClick}
                  disabled={!canGenerateEvidence || evidenceMutation.isPending}
                  data-testid="button-evidence-personas"
                >
                  {evidenceMutation.isPending ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Starting batch...</>
                  ) : (
                    "Select number to generate"
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
          <Card
            className="border-border/40 hover:border-primary/30 cursor-pointer transition-colors"
            onClick={() => setMode("ai")}
            data-testid="card-ai-generate"
          >
            <CardContent className="p-6 flex flex-col items-center text-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-semibold">AI Generate</h3>
              <p className="text-sm text-muted-foreground">Describe your target persona and AI will build the full profile in the background</p>
            </CardContent>
          </Card>

          <Card
            className="border-border/40 hover:border-primary/30 cursor-pointer transition-colors"
            onClick={() => setMode("manual")}
            data-testid="card-manual-create"
          >
            <CardContent className="p-6 flex flex-col items-center text-center gap-3">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <Plus className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="font-semibold">Manual Entry</h3>
              <p className="text-sm text-muted-foreground">Fill in each field yourself for full control over the persona profile</p>
            </CardContent>
          </Card>
        </div>
        <AlertDialog
          open={
            evidenceDialogOpen
            && confirmationBrandRef.current === brandId
            && confirmationBrandVersionRef.current === brandVersionRef.current
          }
          onOpenChange={(open) => {
            if (evidenceRequestInFlightRef.current && open) return;
            setEvidenceDialogOpen(open);
            if (!open) confirmationBrandRef.current = undefined;
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Generate {evidencePersonaCount} {evidencePersonaCount === 1 ? "persona" : "personas"} from customer evidence?</AlertDialogTitle>
              <AlertDialogDescription>
                This adds new personas without replacing current ones. It uses active aggregate evidence and runs in the background. Normal generation credit charges apply.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <label htmlFor="evidence-persona-count" className="text-sm font-medium">Number of personas</label>
              <select
                id="evidence-persona-count"
                data-testid="select-evidence-persona-count"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                value={evidencePersonaCount}
                disabled={evidenceMutation.isPending}
                onChange={(event) => setEvidencePersonaCount(Number(event.target.value))}
              >
                {Array.from({ length: 10 }, (_, index) => (
                  <option key={index + 1} value={index + 1}>{index + 1}</option>
                ))}
              </select>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={evidenceMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  void handleEvidenceConfirm();
                }}
                disabled={evidenceMutation.isPending}
                data-testid="confirm-evidence-personas"
              >
                {evidenceMutation.isPending ? "Starting..." : `Generate ${evidencePersonaCount} ${evidencePersonaCount === 1 ? "persona" : "personas"}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (mode === "ai") {
    return (
      <div className="p-8 max-w-3xl mx-auto space-y-8">
        <div className="flex items-center gap-3 fade-in-up">
          <Button variant="ghost" size="icon" onClick={() => setMode("choose")} data-testid="button-back-choose"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">AI Generate Persona</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Describe your target persona and we'll build the full profile</p>
          </div>
        </div>

        <Card className="border-primary/20 bg-primary/[0.02] fade-in-up-delay-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-primary/70 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Persona Description
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={aiDescription}
              onChange={(e) => setAiDescription(e.target.value)}
              rows={6}
              placeholder="Describe the persona you want to create. For example: 'A health-conscious millennial mom who reads every ingredient label, distrusts big skincare brands, and is actively looking for clean alternatives for her family. She's done her own research and is hard to convince but loyal once she trusts a brand.'"
              className="resize-none"
              disabled={aiGenerating}
              data-testid="input-ai-description"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">The persona will be created in the background — you can navigate away safely</p>
              <Button
                onClick={handleAiGenerate}
                disabled={aiGenerating || !aiDescription.trim()}
                className="rounded-full"
                data-testid="button-ai-generate"
              >
                {aiGenerating ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Generating...</>
                ) : (
                  <><Sparkles className="h-3.5 w-3.5 mr-1.5" />Generate Persona</>
                )}
              </Button>
            </div>
            {aiGenerating && (
              <div className="mt-3 rounded-lg bg-primary/[0.04] border border-primary/15 p-4 space-y-3" data-testid="panel-ai-progress">
                <div className="flex items-center gap-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                  <p
                    key={aiProgressIndex}
                    className="text-sm font-medium text-foreground/80 animate-in fade-in slide-in-from-bottom-1 duration-300"
                    data-testid="text-ai-progress-message"
                  >
                    {AI_PROGRESS_MESSAGES[aiProgressIndex]}
                  </p>
                </div>
                <div className="w-full bg-muted/40 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-primary/50 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${Math.min(8 + aiProgressIndex * (82 / (AI_PROGRESS_MESSAGES.length - 1)), 90)}%` }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground/60">This usually takes 2–5 minutes. You can navigate away — you'll be notified when it's ready.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-8">
      <div className="flex items-center justify-between fade-in-up">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setMode("choose")} data-testid="button-back-choose-manual"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Create Persona</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Build a persuasion profile for campaign planning</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link href={`/brands/${brandId}/personas`}>
            <Button variant="outline" data-testid="button-cancel-create">Cancel</Button>
          </Link>
          <Button
            onClick={() => createMutation.mutate(form)}
            disabled={createMutation.isPending || !form.name.trim()}
            className="rounded-full"
            data-testid="button-submit-persona"
          >
            {createMutation.isPending ? "Saving..." : "Save Persona"}
          </Button>
        </div>
      </div>

      <Card className="border-border/50 fade-in-up-delay-1">
        <CardHeader>
          <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Name *</label>
            <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Ingredient Skeptic Mom" data-testid="input-create-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Status</label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="select-create-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Awareness Stage</label>
              <Select value={form.awarenessStage} onValueChange={(v) => setField("awarenessStage", v)}>
                <SelectTrigger data-testid="select-create-awareness"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AWARENESS_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Summary</label>
            <Textarea value={form.summary} onChange={(e) => setField("summary", e.target.value)} rows={3} placeholder="1-2 sentence mindset description" data-testid="input-create-summary" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/50 fade-in-up-delay-1">
        <CardHeader>
          <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Core Psychology</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Worldview</label>
            <Textarea value={form.worldview} onChange={(e) => setField("worldview", e.target.value)} rows={3} placeholder="How they see the category/world" data-testid="input-create-worldview" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Primary Pain</label>
            <Textarea value={form.primaryPain} onChange={(e) => setField("primaryPain", e.target.value)} rows={3} placeholder="Main frustration or pain point" data-testid="input-create-primaryPain" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Desired Outcome</label>
            <Textarea value={form.desiredOutcome} onChange={(e) => setField("desiredOutcome", e.target.value)} rows={3} placeholder="What they want instead" data-testid="input-create-desiredOutcome" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Deep Fear</label>
            <Textarea value={form.deepFear} onChange={(e) => setField("deepFear", e.target.value)} rows={3} placeholder="What they fear if they keep making wrong choices" data-testid="input-create-deepFear" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/50 fade-in-up-delay-2">
        <CardHeader>
          <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Persuasion Inputs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Core Beliefs</label>
            <ChipEditor items={form.coreBeliefsJson} onChange={(v) => setField("coreBeliefsJson", v)} placeholder="Add a core belief" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Objections</label>
            <ChipEditor items={form.objectionsJson} onChange={(v) => setField("objectionsJson", v)} placeholder="Add an objection" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Trust Triggers</label>
            <ChipEditor items={form.trustTriggersJson} onChange={(v) => setField("trustTriggersJson", v)} placeholder="Add a trust trigger" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Purchase Drivers</label>
            <ChipEditor items={form.purchaseDriversJson} onChange={(v) => setField("purchaseDriversJson", v)} placeholder="Add a purchase driver" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Emotional Hooks</label>
            <ChipEditor items={form.emotionalHooksJson} onChange={(v) => setField("emotionalHooksJson", v)} placeholder="Add an emotional hook" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/50 fade-in-up-delay-2">
        <CardHeader>
          <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Voice & Refinement (Optional)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Language Patterns</label>
            <ChipEditor items={form.languagePatternsJson} onChange={(v) => setField("languagePatternsJson", v)} placeholder="Words/phrases they use or respond to" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Customer Voice Quotes</label>
            <ChipEditor items={form.customerVoiceQuotesJson} onChange={(v) => setField("customerVoiceQuotesJson", v)} placeholder="Real customer language from reviews, comments, etc." />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Secondary Pains</label>
            <ChipEditor items={form.secondaryPainsJson} onChange={(v) => setField("secondaryPainsJson", v)} placeholder="Supporting frustrations" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Exclusions</label>
            <ChipEditor items={form.exclusionsJson} onChange={(v) => setField("exclusionsJson", v)} placeholder="Things not to say / frames to avoid" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={3} placeholder="Internal notes" data-testid="input-create-notes" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 pb-8 fade-in-up-delay-3">
        <Link href={`/brands/${brandId}/personas`}>
          <Button variant="outline" data-testid="button-cancel-create-bottom">Cancel</Button>
        </Link>
        <Button
          onClick={() => createMutation.mutate(form)}
          disabled={createMutation.isPending || !form.name.trim()}
          className="rounded-full"
          data-testid="button-submit-persona-bottom"
        >
          {createMutation.isPending ? "Saving..." : "Save Persona"}
        </Button>
      </div>
    </div>
  );
}
